import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, UserPlus, ShieldCheck, ShieldOff, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" ");
  return "Errore imprevisto. Riprova.";
};

const inputCls =
  "w-full rounded-xl bg-white border border-ink/15 px-4 py-3 text-sm text-ink placeholder:text-ink/35 focus:outline-none focus:border-azure focus:ring-1 focus:ring-azure";

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const token = localStorage.getItem("sf-admin-token") || "";
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: meData }] = await Promise.all([
        axios.get(`${API}/admin/users`, auth),
        axios.get(`${API}/auth/me`, auth),
      ]);
      setUsers(list);
      setMe(meData);
    } catch {
      toast.error("Errore nel caricamento degli utenti");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/users`, form, auth);
      toast.success(`Utente ${form.email} creato`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.email}? Non potrà più accedere all'area admin.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`, auth);
      toast.success("Utente eliminato");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`Resettare il 2FA di ${u.email}? Al prossimo login dovrà riconfigurare Google Authenticator.`)) return;
    try {
      await axios.post(`${API}/admin/users/${u.id}/reset-2fa`, {}, auth);
      toast.success("2FA resettato: al prossimo login verrà chiesto di riconfigurare Google Authenticator");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast.error("Le nuove password non coincidono");
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.next }, auth);
      toast.success("Password aggiornata");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="users-admin">
      <div className="bg-white rounded-2xl border border-ink/10 p-6">
        <h2 className="font-serif font-bold text-xl mb-1 flex items-center gap-2">
          <KeyRound size={18} className="text-azure" /> Cambia la tua password
        </h2>
        <p className="text-sm text-ink/50 mb-5">
          La nuova password vale per il tuo account ({me?.email || "..."}). Minimo 8 caratteri. Il 2FA con Google Authenticator resta attivo.
        </p>
        <form onSubmit={changePw} className="grid sm:grid-cols-3 gap-3" data-testid="change-password-form">
          <input
            type="password"
            required
            value={pwForm.current}
            onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
            placeholder="Password attuale"
            autoComplete="current-password"
            data-testid="change-password-current-input"
            className={inputCls}
          />
          <input
            type="password"
            required
            minLength={8}
            value={pwForm.next}
            onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
            placeholder="Nuova password (min. 8)"
            autoComplete="new-password"
            data-testid="change-password-new-input"
            className={inputCls}
          />
          <input
            type="password"
            required
            minLength={8}
            value={pwForm.confirm}
            onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
            placeholder="Conferma nuova password"
            autoComplete="new-password"
            data-testid="change-password-confirm-input"
            className={inputCls}
          />
          <div className="sm:col-span-3">
            <button
              type="submit"
              disabled={pwSaving}
              data-testid="change-password-submit-btn"
              className="inline-flex items-center gap-2 bg-ink hover:bg-navy disabled:opacity-60 text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
            >
              {pwSaving ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
              Aggiorna password
            </button>
          </div>
        </form>
      </div>
      <div className="bg-white rounded-2xl border border-ink/10 p-6">
        <h2 className="font-serif font-bold text-xl mb-1 flex items-center gap-2">
          <UserPlus size={18} className="text-azure" /> Crea un nuovo utente admin
        </h2>
        <p className="text-sm text-ink/50 mb-5">
          Il nuovo utente potrà accedere all'area admin con email e password. Al primo login configurerà Google Authenticator (2FA obbligatorio per tutti).
        </p>
        <form onSubmit={create} className="grid sm:grid-cols-3 gap-3" data-testid="user-create-form">
          <input
            type="text"
            required
            minLength={2}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Nome e cognome"
            data-testid="user-create-name-input"
            className={inputCls}
          />
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="Email"
            data-testid="user-create-email-input"
            className={inputCls}
          />
          <input
            type="password"
            required
            minLength={8}
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Password (min. 8 caratteri)"
            data-testid="user-create-password-input"
            className={inputCls}
          />
          <div className="sm:col-span-3">
            <button
              type="submit"
              disabled={saving}
              data-testid="user-create-submit-btn"
              className="inline-flex items-center gap-2 bg-azure hover:bg-brandblue disabled:opacity-60 text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
              Crea utente
            </button>
          </div>
        </form>
      </div>

      <div>
        <h2 className="font-serif font-bold text-xl mb-4">Utenti admin ({users.length})</h2>
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={28} className="animate-spin text-azure" />
          </div>
        ) : (
          <div className="space-y-3" data-testid="users-list">
            {users.map((u) => (
              <div
                key={u.id}
                data-testid={`user-row-${u.email}`}
                className="flex items-center gap-4 bg-white rounded-2xl border border-ink/10 px-5 py-4"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">
                    {u.name}
                    {me?.email === u.email && (
                      <span className="ml-2 text-[11px] font-semibold rounded-full px-2.5 py-0.5 bg-azure/10 text-brandblue">tu</span>
                    )}
                  </p>
                  <p className="text-xs text-ink/45 mt-0.5 flex items-center gap-1.5">
                    {u.email} · <ShieldCheck size={12} className="text-azure" /> 2FA Google Authenticator
                  </p>
                </div>
                <button
                  onClick={() => reset2fa(u)}
                  className="p-2 text-ink/40 hover:text-gold transition-colors"
                  aria-label={`Reset 2FA ${u.email}`}
                  title="Reset 2FA (riconfigura Google Authenticator al prossimo login)"
                  data-testid={`user-reset2fa-btn-${u.email}`}
                >
                  <ShieldOff size={17} />
                </button>
                {me?.email !== u.email && (
                  <button
                    onClick={() => remove(u)}
                    className="p-2 text-ink/40 hover:text-red-500 transition-colors"
                    aria-label={`Elimina ${u.email}`}
                    data-testid={`user-delete-btn-${u.email}`}
                  >
                    <Trash2 size={17} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
